import React from 'react';
import { ResourceSummary } from '../../services/awsService';
import { MonthlyCostData } from '../../services/billingService';
import {
  CircleStackIcon,
  CubeIcon,
  CurrencyDollarIcon,
  CpuChipIcon,
  ServerIcon,
  FolderIcon,
  DocumentTextIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';

// Custom S3 Bucket Icon using external SVG
const S3BucketIcon = ({ className }: { className?: string }) => (
  <img src="/s3-bucket.svg" alt="S3" className={className} />
);

// Custom RDS Icon using external SVG
const RDSIcon = ({ className }: { className?: string }) => (
  <img src="/rds.svg" alt="RDS" className={className} />
);

// Custom EC2 Icon using external SVG
const EC2Icon = ({ className }: { className?: string }) => (
  <img src="/ec2.svg" alt="EC2" className={className} />
);

// Custom EBS Icon using external SVG
const EBSIcon = ({ className }: { className?: string }) => (
  <img src="/ebs.svg" alt="EBS" className={className} />
);

// Custom EKS Icon (K with design)
const EKSIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M9 12L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M9 12L15 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Custom ECS Icon (Container with layers)
const ECSIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
    <rect x="3" y="9" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
    <rect x="3" y="15" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
    <circle cx="6" cy="6" r="1" fill="currentColor"/>
    <circle cx="6" cy="12" r="1" fill="currentColor"/>
    <circle cx="6" cy="18" r="1" fill="currentColor"/>
  </svg>
);

// Custom CloudFront Icon (Global network/CDN icon)
const CloudFrontIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
    <path d="M3.5 9H20.5" stroke="currentColor" strokeWidth="2"/>
    <path d="M3.5 15H20.5" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 3C12 3 8 6 8 12C8 18 12 21 12 21" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 3C12 3 16 6 16 12C16 18 12 21 12 21" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

// Custom VPC Icon (Network icon)
const VPCIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="8" width="18" height="8" rx="1" stroke="currentColor" strokeWidth="2"/>
    <circle cx="8" cy="12" r="2" fill="currentColor"/>
    <circle cx="16" cy="12" r="2" fill="currentColor"/>
    <path d="M10 12H14" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 4V8" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 16V20" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

// Custom Lambda Icon (λ symbol)
const LambdaIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 19L10 5L12 12L14 5L19 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

interface ResourceSummaryCardsProps {
  summary: ResourceSummary;
  totalResources: {
    totalVcpus: number;
    totalMemory: number;
    totalStorage: number;
  };
  totalMonthlyCost: number;
  billingData?: MonthlyCostData | null;
}

const ResourceSummaryCards: React.FC<ResourceSummaryCardsProps> = ({
  summary,
  totalResources,
  totalMonthlyCost,
  billingData
}) => {
  console.log('ResourceSummaryCards - billingData:', billingData);
  console.log('ResourceSummaryCards - totalMonthlyCost:', totalMonthlyCost);
  interface CardDetail {
    service: string;
    value: string | number;
  }

  const mainCards: Array<{
    title: string;
    value: string | number;
    subtitle: string;
    icon: any;
    color: string;
    details: string | CardDetail[] | React.ReactNode;
    isHighlighted: boolean;
    hasBillingData?: boolean;
  }> = [
    {
      title: 'Total vCPUs',
      value: totalResources.totalVcpus || 0,
      subtitle: 'All compute cores',
      icon: CpuChipIcon,
      color: 'indigo',
      details: [
        { service: 'EC2', value: summary.ec2.totalVcpus || 0 },
        { service: 'EKS', value: summary.eks.totalVcpus || 0 },
        { service: 'RDS', value: summary.rds?.totalVcpus || 0 }
      ],
      isHighlighted: true
    },
    {
      title: 'Total Memory',
      value: `${Math.round(totalResources.totalMemory || 0)}GB`,
      subtitle: 'All RAM',
      icon: CubeIcon,
      color: 'green',
      details: [
        { service: 'EC2', value: `${Math.round(summary.ec2.totalMemory || 0)}GB` },
        { service: 'EKS', value: `${Math.round(summary.eks.totalMemory || 0)}GB` },
        { service: 'RDS', value: `${Math.round(summary.rds?.totalMemory || 0)}GB` }
      ],
      isHighlighted: true
    },
    {
      title: 'Total Storage',
      value: `${Math.round(totalResources.totalStorage || 0)}GB`,
      subtitle: 'All storage',
      icon: CircleStackIcon,
      color: 'purple',
      details: [
        { service: 'EBS', value: `${Math.round(summary.ebs.totalGiB || 0)}GB` },
        { service: 'S3', value: `${Math.round(summary.s3.totalGiB || 0)}GB` },
        { service: 'RDS', value: `${Math.round(summary.rds?.totalStorageGB || 0)}GB` },
        { service: 'FSx', value: `${Math.round(summary.fsx?.totalStorageGB || 0)}GB` },
        { service: 'EFS', value: `${Math.round(summary.efs?.totalGiB || 0)}GB` }
      ],
      isHighlighted: true
    },
    {
      title: 'Monthly Spend*',
      value: (() => {
        if (billingData) {
          console.log('Using billing data - previous month spend:', billingData.previousMonth.actualSpend);
          const formattedValue = `$${Number(billingData.previousMonth.actualSpend).toFixed(2)}`;
          console.log('Formatted value:', formattedValue);
          return formattedValue;
        } else {
          console.log('No billing data - using calculated cost:', totalMonthlyCost);
          return `$${(totalMonthlyCost || 0).toFixed(2)}`;
        }
      })(),
      subtitle: billingData ? billingData.previousMonth.period : 'Estimated cost',
      icon: CurrencyDollarIcon,
      color: 'red',
      details: billingData ? (
        <div className="flex items-center justify-between">
          <span>{billingData.currentMonth.period.split(' ')[0]} Projected: ${(Number(billingData.currentMonth.projectedSpend) || 0).toFixed(2)}</span>
          <span className={`flex items-center ${billingData.percentageChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {billingData.percentageChange > 0 ? (
              <ArrowUpIcon className="h-3 w-3 mr-1" />
            ) : (
              <ArrowDownIcon className="h-3 w-3 mr-1" />
            )}
            {Math.abs(Number(billingData.percentageChange) || 0).toFixed(1)}%
          </span>
        </div>
      ) : `Compute: $${((summary.ec2.monthlyComputeCost || 0) + (summary.eks.monthlyComputeCost || 0) + (summary.rds?.totalMonthlyCost || 0)).toFixed(2)} • Storage: $${((summary.ebs.monthlyCost || 0) + (summary.s3.monthlyCost || 0) + (summary.fsx?.monthlyCost || 0) + (summary.efs?.monthlyCost || 0)).toFixed(2)} • Other: $${((summary.dynamodb?.monthlyCost || 0) + (summary.cloudwatchLogs?.monthlyCost || 0) + (summary.cloudfront?.monthlyCost || 0) + (summary.ecs?.monthlyCost || 0)).toFixed(2)}`,
      isHighlighted: true,
      hasBillingData: !!billingData
    }
  ];

  // Organized into categories and sorted alphabetically
  const computeServices = [
    {
      title: 'EC2',
      value: summary.ec2.instances,
      subtitle: `${summary.ec2.runningInstances} running`,
      icon: EC2Icon,
      color: 'blue',
      tooltip: `${summary.ec2.instanceTypes} types across ${summary.ec2.regions} regions`
    },
    {
      title: 'ECS',
      value: summary.ecs?.clusters || 0,
      subtitle: `${summary.ecs?.services || 0} services`,
      icon: ECSIcon,
      color: 'purple',
      tooltip: (summary.ecs?.clusters || 0) > 0
        ? `${summary.ecs?.runningTasks || 0} running tasks • ${summary.ecs?.desiredCount || 0} desired • $${(summary.ecs?.monthlyCost || 0).toFixed(2)}/month`
        : 'No ECS clusters'
    },
    {
      title: 'EKS',
      value: summary.eks.clusters,
      subtitle: `${summary.eks.totalNodes || 0} nodes`,
      icon: EKSIcon,
      color: 'teal',
      tooltip: summary.eks.totalNodes > 0 
        ? `${summary.eks.nodeGroups} node groups • ${summary.eks.totalVcpus || 0} vCPUs • ${Math.round(summary.eks.totalMemory || 0)}GB RAM`
        : `${summary.eks.nodeGroups} node groups`
    }
  ];

  const storageServices = [
    {
      title: 'EBS',
      value: `${Math.round(summary.ebs.totalGiB || 0)}GB`,
      subtitle: `${summary.ebs.volumes} volumes`,
      icon: EBSIcon,
      color: 'purple',
      tooltip: `$${(summary.ebs.monthlyCost || 0).toFixed(2)}/month`
    },
    {
      title: 'EFS',
      value: `${Math.round(summary.efs?.totalGiB || 0)}GB`,
      subtitle: `${summary.efs?.fileSystems || 0} systems`,
      icon: FolderIcon,
      color: 'green',
      tooltip: (summary.efs?.fileSystems || 0) > 0
        ? `${summary.efs?.totalMountTargets || 0} mount targets • $${(summary.efs?.monthlyCost || 0).toFixed(2)}/month`
        : 'No EFS file systems'
    },
    {
      title: 'FSx',
      value: summary.fsx?.fileSystems || 0,
      subtitle: `${summary.fsx?.types || 0} types`,
      icon: ServerIcon,
      color: 'indigo',
      tooltip: (summary.fsx?.fileSystems || 0) > 0
        ? `${summary.fsx?.totalStorageGB || 0}GB storage • $${(summary.fsx?.monthlyCost || 0).toFixed(2)}/month`
        : 'No FSx file systems'
    },
    {
      title: 'S3',
      value: `${Math.round(summary.s3.totalGiB || 0)}GB`,
      subtitle: `${summary.s3.buckets} buckets`,
      icon: S3BucketIcon,
      color: 'orange',
      tooltip: `${(summary.s3.objects || 0).toLocaleString()} objects • Standard: ${Math.round(summary.s3.standardGiB || 0)}GB • $${(summary.s3.monthlyCost || 0).toFixed(2)}/month`
    },
    {
      title: 'S3 Glacier',
      value: `${Math.round((summary.s3.glacierGiB || 0) + (summary.s3.glacierDeepArchiveGiB || 0))}GB`,
      subtitle: `Archive storage`,
      icon: S3BucketIcon,
      color: 'blue',
      tooltip: `Glacier: ${Math.round(summary.s3.glacierGiB || 0)}GB • Deep Archive: ${Math.round(summary.s3.glacierDeepArchiveGiB || 0)}GB`
    }
  ];

  const databaseServices = [
    {
      title: 'DynamoDB',
      value: summary.dynamodb?.tables || 0,
      subtitle: `${Math.round(summary.dynamodb?.totalGiB || 0)}GB`,
      icon: CircleStackIcon,
      color: 'purple',
      tooltip: (summary.dynamodb?.tables || 0) > 0
        ? `${(summary.dynamodb?.totalItems || 0).toLocaleString()} items • $${(summary.dynamodb?.monthlyCost || 0).toFixed(2)}/month`
        : 'No DynamoDB tables'
    },
    {
      title: 'RDS',
      value: summary.rds?.instances || 0,
      subtitle: `${summary.rds?.availableInstances || 0} available`,
      icon: RDSIcon,
      color: 'yellow',
      tooltip: summary.rds?.instances > 0 
        ? `${summary.rds?.totalVcpus || 0} vCPUs • ${Math.round(summary.rds?.totalMemory || 0)}GB RAM • ${summary.rds?.totalStorageGB || 0}GB storage`
        : 'No RDS databases'
    }
  ];

  // CDN & Networking Services
  const cdnNetworkingServices = [
    {
      title: 'CloudFront',
      value: summary.cloudfront?.distributions || 0,
      subtitle: `${summary.cloudfront?.enabledDistributions || 0} enabled`,
      icon: CloudFrontIcon,
      color: 'blue',
      tooltip: (summary.cloudfront?.distributions || 0) > 0
        ? `${Math.round(summary.cloudfront?.totalMonthlyDataTransferGB || 0)}GB/mo transfer • ${(summary.cloudfront?.totalMonthlyRequests || 0).toLocaleString()} req/mo • $${(summary.cloudfront?.monthlyCost || 0).toFixed(2)}/month`
        : 'No distributions'
    },
    {
      title: 'VPC',
      value: summary.vpc?.vpcs || 0,
      subtitle: `${summary.vpc?.customVpcs || 0} custom`,
      icon: VPCIcon,
      color: 'indigo',
      tooltip: (summary.vpc?.vpcs || 0) > 0
        ? `${summary.vpc?.totalSubnets || 0} subnets • ${summary.vpc?.totalNatGateways || 0} NAT gateways • ${summary.vpc?.totalVpnConnections || 0} VPN connections • $${(summary.vpc?.monthlyCost || 0).toFixed(2)}/month`
        : 'No VPCs'
    }
  ];

  // Other Services
  const otherServices = [
    {
      title: 'CloudWatch',
      value: summary.cloudwatchLogs?.logGroups || 0,
      subtitle: `${summary.cloudwatchLogs?.logGroups || 0} log groups`,
      icon: DocumentTextIcon,
      color: 'orange',
      tooltip: (summary.cloudwatchLogs?.logGroups || 0) > 0
        ? `${Math.round(summary.cloudwatchLogs?.totalStoredGiB || 0)}GB stored • ${Math.round(summary.cloudwatchLogs?.totalIncomingGB || 0)}GB/mo incoming • $${(summary.cloudwatchLogs?.monthlyCost || 0).toFixed(2)}/month`
        : 'No log groups'
    },
    {
      title: 'Lambda',
      value: summary.lambda?.functions || 0,
      subtitle: `${summary.lambda?.runtimes || 0} runtimes`,
      icon: LambdaIcon,
      color: 'purple',
      tooltip: (summary.lambda?.functions || 0) > 0
        ? `${(summary.lambda?.totalInvocations || 0).toLocaleString()} invocations • ${Math.round(summary.lambda?.avgDuration || 0)}ms avg • $${(summary.lambda?.monthlyCost || 0).toFixed(2)}/month`
        : 'No Lambda functions'
    }
  ];

  const colorClasses = {
    blue: {
      icon: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      value: 'text-blue-900 dark:text-blue-100',
      badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
    },
    indigo: {
      icon: 'text-indigo-600 dark:text-indigo-400',
      bg: 'bg-indigo-50 dark:bg-indigo-900/20',
      value: 'text-indigo-900 dark:text-indigo-100',
      badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300'
    },
    green: {
      icon: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-50 dark:bg-green-900/20',
      value: 'text-green-900 dark:text-green-100',
      badge: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
    },
    purple: {
      icon: 'text-purple-600 dark:text-purple-400',
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      value: 'text-purple-900 dark:text-purple-100',
      badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300'
    },
    orange: {
      icon: 'text-orange-600 dark:text-orange-400',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      value: 'text-orange-900 dark:text-orange-100',
      badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300'
    },
    teal: {
      icon: 'text-teal-600 dark:text-teal-400',
      bg: 'bg-teal-50 dark:bg-teal-900/20',
      value: 'text-teal-900 dark:text-teal-100',
      badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300'
    },
    yellow: {
      icon: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      value: 'text-yellow-900 dark:text-yellow-100',
      badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
    },
    red: {
      icon: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-50 dark:bg-red-900/20',
      value: 'text-red-900 dark:text-red-100',
      badge: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {mainCards.map((card, index) => {
          const Icon = card.icon;
          const colors = colorClasses[card.color as keyof typeof colorClasses];
          
          return (
            <div
              key={index}
              className={`${
                card.isHighlighted 
                  ? 'bg-slate-900 border border-slate-700' 
                  : 'bg-white dark:bg-gray-800'
              } overflow-hidden shadow rounded-lg ${card.isHighlighted ? 'shadow-lg' : ''}`}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-md ${card.isHighlighted ? 'bg-slate-800' : colors.bg}`}>
                      <Icon className={`h-6 w-6 ${card.isHighlighted ? 'text-white' : colors.icon}`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className={`text-sm font-medium ${card.isHighlighted ? 'text-gray-300' : 'text-gray-500 dark:text-gray-400'} truncate`}>
                        {card.title}
                      </dt>
                      <dd className={`${card.isHighlighted ? 'text-2xl' : 'text-lg'} font-medium ${card.isHighlighted ? 'text-white' : colors.value}`}>
                        {card.value}
                      </dd>
                      <dd className={`text-sm ${card.isHighlighted ? 'text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                        {card.subtitle}
                      </dd>
                    </dl>
                  </div>
                </div>
                <div className="mt-3">
                  {Array.isArray(card.details) ? (
                    <div className="mt-3">
                      <div className={`grid ${
                        card.details.filter(item => {
                          if (card.title === 'Total Storage') {
                            const numValue = parseInt(item.value.toString());
                            return numValue > 0;
                          }
                          return true;
                        }).length <= 3 ? 'grid-cols-3' : 'grid-cols-3 sm:grid-cols-5'
                      } gap-2`}>
                        {card.details.filter(item => {
                          // Filter out services with 0 values for storage
                          if (card.title === 'Total Storage') {
                            const numValue = parseInt(item.value.toString());
                            return numValue > 0;
                          }
                          return true;
                        }).map((item, idx) => (
                          <div key={idx} className="flex flex-col items-center justify-center p-2 rounded-lg bg-gray-800/30 backdrop-blur-sm border border-gray-700/50">
                            <div className={`text-xs font-medium ${card.isHighlighted ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'} uppercase tracking-wider`}>
                              {item.service}
                            </div>
                            <div className={`text-base font-bold mt-1 ${card.isHighlighted ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                              {item.value}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className={`text-xs ${card.isHighlighted ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {card.details}
                    </div>
                  )}
                  {card.title === 'Monthly Spend*' && billingData && (
                    <div className={`text-xs mt-2 ${card.isHighlighted ? 'text-gray-500' : 'text-gray-400 dark:text-gray-500'}`}>
                      *Source: AWS Cost Explorer
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Service Resources by Category - All in One Card */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="space-y-6">
          {/* Compute and Database Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compute Services */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-xs font-extrabold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-4">Compute</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {computeServices.map((card, index) => {
                const Icon = card.icon;
                const colors = colorClasses[card.color as keyof typeof colorClasses];
                
                return (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    title={card.tooltip}
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <div className={`p-2 rounded-md ${colors.bg}`}>
                        <Icon className={`h-5 w-5 ${colors.icon}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {card.title}
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {card.value}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {card.subtitle}
                        </p>
                      </div>
                    </div>
                    {/* Tooltip on hover */}
                    <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded p-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 text-center">
                      {card.tooltip}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>

            {/* Database Services */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-xs font-extrabold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-4">Database</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {databaseServices.map((card, index) => {
                const Icon = card.icon;
                const colors = colorClasses[card.color as keyof typeof colorClasses];
                
                return (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    title={card.tooltip}
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <div className={`p-2 rounded-md ${colors.bg}`}>
                        <Icon className={`h-5 w-5 ${colors.icon}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {card.title}
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {card.value}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {card.subtitle}
                        </p>
                      </div>
                    </div>
                    {/* Tooltip on hover */}
                    <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded p-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 text-center">
                      {card.tooltip}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </div>

          {/* Storage Services - Full Width */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <h3 className="text-xs font-extrabold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-4">Storage</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {storageServices.map((card, index) => {
              const Icon = card.icon;
              const colors = colorClasses[card.color as keyof typeof colorClasses];
              
              return (
                <div
                  key={index}
                  className="relative group cursor-pointer"
                  title={card.tooltip}
                >
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <div className={`p-2 rounded-md ${colors.bg}`}>
                      <Icon className={`h-5 w-5 ${colors.icon}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {card.title}
                      </p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {card.value}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {card.subtitle}
                      </p>
                    </div>
                  </div>
                  {/* Tooltip on hover */}
                  <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded p-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 text-center">
                    {card.tooltip}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>

          {/* CDN & Networking and Other Services Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* CDN & Networking Services */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-xs font-extrabold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-4">CDN & Networking</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {cdnNetworkingServices.map((card, index) => {
                const Icon = card.icon;
                const colors = colorClasses[card.color as keyof typeof colorClasses];
                
                return (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    title={card.tooltip}
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <div className={`p-2 rounded-md ${colors.bg}`}>
                        <Icon className={`h-5 w-5 ${colors.icon}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {card.title}
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {card.value}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {card.subtitle}
                        </p>
                      </div>
                    </div>
                    {/* Tooltip on hover */}
                    <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded p-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 text-center">
                      {card.tooltip}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>

            {/* Other Services */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-xs font-extrabold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-4">Other Services</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {otherServices.map((card, index) => {
                const Icon = card.icon;
                const colors = colorClasses[card.color as keyof typeof colorClasses];
                
                return (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    title={card.tooltip}
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <div className={`p-2 rounded-md ${colors.bg}`}>
                        <Icon className={`h-5 w-5 ${colors.icon}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {card.title}
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {card.value}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {card.subtitle}
                        </p>
                      </div>
                    </div>
                    {/* Tooltip on hover */}
                    <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded p-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 text-center">
                      {card.tooltip}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceSummaryCards;